import React from 'react'
import { useSignal } from '@preact/signals-react'
import { Autocomplete, Box, TextField } from '@mui/material'
import { AmchartSeeker } from '@ui-seeker/core'
import { AppCardTitle, AppResultsSkeleton } from '@components'
import { shades } from '@palette'
import { isOneWayLoading, oneWayData, oneWayPriceMode } from 'Pages/AnalyzersV2/OneWay/@signals'
import { oneWayAxisLabel } from 'Pages/AnalyzersV2/OneWay/Table/axis'

// the same eight tones as before, read from the palette
const SERIES_COLORS = ['blue', 'green', 'orange', 'red', 'purple', 'cyan', 'deepOrange', 'brown'].map(tone => shades[tone][400])

export default function OneWayChart() {
	const { results, headers, subHeaders, subColumns } = oneWayData.value || {}

	const selectedRow = useSignal(null)
	const selectedLor = useSignal(null)

	if (isOneWayLoading.value) return <AppResultsSkeleton chart height={360}/>
	if (!results?.length || !headers?.length) return null

	// the rows are webs or brands depending on the toggle the response was built with
	const axisLabel = oneWayAxisLabel()
	const rowNames = results.map(r => r.name)
	const rowName = rowNames.includes(selectedRow.value) ? selectedRow.value : rowNames[0]
	const lor = subHeaders?.includes(selectedLor.value) ? selectedLor.value : subHeaders?.[0] ?? null

	const row = results.find(r => r.name === rowName)

	const priceField = oneWayPriceMode.value === 'roundTrip' ? 'oneWayPrice' : 'fee'

	const chartData = headers.map(date => {
		const point = { date }
		subColumns?.forEach(acriss => {
			const entry = row?.[date]?.[String(lor)]?.find(e => e.acriss === acriss)
			const price = entry?.[priceField]
			point[acriss] = (price != null && price !== 'N/A') ? parseFloat(price) : null
		})
		return point
	})

	const keySeries = (subColumns || []).map((acriss, i) => ({
		dataKey: acriss,
		color: SERIES_COLORS[i % SERIES_COLORS.length],
	}))

	return <AmchartSeeker
		rootID="OneWayFeeChart"
		title={<AppCardTitle icon="chart-line" title="One Way Fee Evolution"/>}
		data={chartData}
		series={keySeries.map(({ dataKey, color }) => ({
			name: dataKey,
			yField: dataKey,
			xField: 'date',
			color,
			connect: true,
			type: 'line',
			tooltip: { pointerOrientation: 'horizontal' },
			tension: 1,
			// The pickup dates of a report are days apart: one line across them, as in Position over time
			autoGapCount: Infinity,
		}))}
		sx={{ height: 360 }}
		cardProps={{
			actions: <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 2 }}>
				<Autocomplete
					size="small"
					options={rowNames}
					value={rowName}
					onChange={(_, v) => { if (v) selectedRow.value = v }}
					disableClearable
					sx={{ minWidth: 220 }}
					renderInput={params => <TextField {...params} label={axisLabel} />}
				/>
				<Autocomplete
					size="small"
					options={subHeaders || []}
					getOptionLabel={o => `${o}`}
					isOptionEqualToValue={(o, v) => String(o) === String(v)}
					value={lor}
					onChange={(_, v) => { if (v != null) selectedLor.value = v }}
					disableClearable
					sx={{ minWidth: 100 }}
					renderInput={params => <TextField {...params} label="LOR" />}
				/>
			</Box>
		}}
		chartProps={{
			showBullets: true,
			enabledPDF: false,
			numberFormat: '#,###.00',
			dateFormatReceived: 'yyyyMMdd',
			// No scrollbars, as in Position over time
			scrollbarX: { hidden: true },
			xAxis: {
				categoryField: 'date',
				isDateAxis: true,
				dateFormat: 'dd MMM yyyy (EEE)',
				title: 'Pickup Date',
				baseInterval: { timeUnit: 'day', count: 1 },
				// Days on the axis, never "2026 W43": the chart labels weeks with that format, so it only gets day steps
				gridIntervals: [1, 2, 3, 5, 7, 14].map(count => ({ timeUnit: 'day', count })).concat({ timeUnit: 'month', count: 1 }),
			},
			yAxis: { title: oneWayPriceMode.value === 'roundTrip' ? 'One Way Price' : 'Fee' },
		}}
	/>
}
